import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Wrench, Truck, Flag, ClipboardCheck, LifeBuoy, BellRing, Car, RotateCcw } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DeliveryPanel } from '../components/ServiceInspection';
import { DeliveryInspection } from '../components/DeliveryInspection';
import { Service as RepairOrders } from './Modules';
import { InspectionQueue } from './service/InspectionQueue';
import { InspectionDetail } from './service/InspectionDetail';
import { ReconBoard } from './service/ReconBoard';
import { MechanicQueue } from './service/MechanicQueue';
import { SoldReview } from './service/SoldReview';
import { elapsedH, DELIVER_ROLES as PS_DELIVER_ROLES } from './PostSale';

const SERVICE_EDIT_ROLES = ['Owner', 'Administrator', 'General Manager', 'Service Manager', 'Service Advisor', 'Service Inspector'];
const DELIVER_ROLES = ['Owner', 'Administrator', 'General Manager', 'Sales Manager'];

const KPI = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3 glow-border card-hover">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4.5 h-4.5" /></div>
    <div><p className="text-[10px] tracking-widest text-muted-foreground/70 font-bold uppercase">{label}</p><p className="font-display font-bold text-xl text-foreground tabular-nums">{value ?? '—'}</p></div>
  </div>
);

const STAGE_LABEL = {
  CONTRACT_SIGNED: 'Contract Assigned', WAITING_WARRANTY: 'Aguardando garantia', WAITING_INSURANCE: 'Aguardando seguro',
  WAITING_TITLE: 'Aguardando title', WAITING_REGISTRATION: 'Aguardando registro', REGISTRATION_COMPLETE: 'Registro concluído',
  FUNDING: 'Aguardando funding', SERVICE: 'Liberado p/ entrega', READY_FOR_DELIVERY: 'Ready for delivery', DELIVERED: 'Entregue',
};

const DealCard = ({ d, onOpen }) => {
  const svc = d.service || { checklist: [], photos: [] };
  const done = svc.checklist.filter((c) => c.checked).length;
  return (
    <button data-testid={`svc-deal-${d.id}`} draggable onDragStart={(e) => e.dataTransfer.setData('svcDealId', d.id)} onClick={() => onOpen(d.id)} className="w-full text-left rounded-lg border border-border bg-popover p-3.5 glow-border transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-glow cursor-grab active:cursor-grabbing">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-sm truncate">{d.customer_name}</p>
        <span className="text-[10px] text-muted-foreground/70">{d.id}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{d.vehicle_desc} · #{d.stock}</p>
      <p className="mono text-[10px] t-label truncate" data-testid={`svc-card-vin-${d.id}`}>VIN {d.vin || '—'}</p>
      <p className="text-[11px] text-muted-foreground/70 mt-0.5">Vendedor: {d.salesperson_name}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mt-1">{STAGE_LABEL[d.status] || d.status}{d.deal_age ? ` · ${d.deal_age}d` : ''}</p>
      {['requested', 'in_progress'].includes(d.service_status) && (
        <div className="mt-2.5 flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-muted/50 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${svc.checklist.length ? (done / svc.checklist.length) * 100 : 0}%` }} /></div>
          <span className="text-[10px] font-bold text-muted-foreground">{done}/{svc.checklist.length}</span>
        </div>
      )}
      {d.service_status === 'ready' && d.status === 'READY_FOR_DELIVERY' && <p className="mt-2.5 text-[11px] font-bold text-emerald-400 uppercase">🚗 Ready — aguardando entrega</p>}
      {d.service_status === 'ready' && d.status !== 'READY_FOR_DELIVERY' && d.status !== 'DELIVERED' && <p className="mt-2.5 text-[11px] font-bold text-amber-400 uppercase">✓ Inspeção OK — aguardando registro/funding</p>}
      {d.status === 'DELIVERED' && <p className="mt-2.5 text-[11px] font-bold text-muted-foreground uppercase">🏁 Delivered {d.delivery?.date} {d.delivery?.time}</p>}
    </button>
  );
};

const PsCard = ({ c, onDeliver, canDeliver }) => (
  <div data-testid={`svc-ps-${c.id}`} className="rounded-lg border border-orange-400/30 bg-popover p-3.5">
    <div className="flex items-center justify-between gap-2">
      <p className="font-semibold text-sm truncate">{c.cliente}</p>
      <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-orange-400/15 text-orange-400 shrink-0">PÓS-VENDA</span>
    </div>
    <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.veiculo || '—'} · {c.tipo}</p>
    {c.reclamacao && <p className="text-[11px] text-muted-foreground/70 mt-1 line-clamp-2">{c.reclamacao}</p>}
    {c.in_workshop && !c.delivered && (
      <p className="text-[11px] font-black text-amber-400 mt-1.5 animate-pulse">⏱ {elapsedH(c.workshop_started_at)} na oficina{c.eta_date ? ` · prev. ${c.eta_date.split('-').reverse().join('/')}` : ''}</p>
    )}
    {c.delivered ? (
      <p className="text-[11px] font-bold text-muted-foreground mt-1.5 uppercase">🏁 Entregue · {c.workshop_hours}h na oficina</p>
    ) : canDeliver && (
      <button data-testid={`svc-ps-deliver-${c.id}`} onClick={() => onDeliver(c)} className="mt-2 w-full text-[11px] font-black py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors">✓ MARCAR ENTREGUE</button>
    )}
  </div>
);

const Column = ({ title, cls, deals, onOpen, testid, onDropCard, extra }) => {
  const [over, setOver] = useState(false);
  return (
    <div className={`flex-1 min-w-[280px] rounded-xl border bg-muted/15 p-3 transition-all duration-200 ${over && onDropCard ? 'kanban-active' : 'border-border/60'}`} data-testid={testid}
      onDragOver={(e) => { e.preventDefault(); if (onDropCard) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData('svcDealId'); if (id && onDropCard) onDropCard(id); }}>
      <div className="flex items-center justify-between mb-3 px-1">
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-full ${cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current neon-dot" />{title}</span>
        <span className="text-xs font-semibold text-primary bg-primary/12 px-2 py-0.5 rounded-full">{deals.length + (extra?.length || 0)}</span>
      </div>
      <div className="space-y-2.5">
        {deals.map((d) => <DealCard key={d.id} d={d} onOpen={onOpen} />)}
        {extra}
        {deals.length === 0 && !(extra?.length) && <p className="text-[12px] text-muted-foreground/50 text-center py-6">Vazio</p>}
      </div>
    </div>
  );
};

export const InspectionDialog = ({ dealId, open, onClose, onChanged }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/deals/${dealId}`); setD(data.deal); } catch (e) {}
  }, [dealId]);
  useEffect(() => { if (open && dealId) { setD(null); load(); } }, [open, dealId, load]);

  const canInspect = d && (SERVICE_EDIT_ROLES.includes(user?.role) || d.salesperson_id === user?.user_id);
  const canDeliver = d && (DELIVER_ROLES.includes(user?.role) || d.salesperson_id === user?.user_id);
  const refresh = () => { load(); onChanged(); };
  const canReopen = d && SERVICE_EDIT_ROLES.includes(user?.role) && d.sold_review && ['requested', 'in_progress'].includes(d.service_status);
  const reopen = async () => {
    if (!window.confirm('Voltar este veículo para a Revisão dos Vendidos? Ele sai da fila de Delivery Inspection.')) return;
    try { await api.post(`/service/sold-review/${dealId}/reopen`); toast({ title: 'Veículo voltou para a Revisão dos Vendidos' }); onChanged(); onClose(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} overlayClassName="z-[110]" onPointerDownOutside={(e) => { if (e.target?.closest?.('[data-testid=svc-photo-zoom]')) e.preventDefault(); }} onInteractOutside={(e) => { if (e.target?.closest?.('[data-testid=svc-photo-zoom]')) e.preventDefault(); }} className="dlg-fullscreen z-[111] bg-card border-border text-foreground sm:max-w-2xl sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="svc-inspection-dialog">
        {!d ? (
          <><DialogHeader><DialogTitle className="sr-only">Carregando</DialogTitle></DialogHeader><p className="text-muted-foreground text-sm py-8 text-center">Carregando...</p></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-black text-xl uppercase" data-testid="svc-dialog-title">
                {d.customer_name} <span className="text-muted-foreground text-sm font-medium normal-case">· {d.vehicle_desc} · #{d.stock} · {d.id}</span>
                <span className="basis-full mono text-[11px] font-medium normal-case t-label" data-testid="svc-dialog-vin">VIN {d.vin || '—'}</span>
              </DialogTitle>
            </DialogHeader>
            <DeliveryPanel d={d} onChanged={refresh} canDeliver={canDeliver} />
            <DeliveryInspection d={d} onChanged={refresh} canEdit={canInspect} />
            {canReopen && <button data-testid="svc-back-to-review" onClick={reopen} className="ent-btn w-full justify-center"><RotateCcw className="w-3.5 h-3.5" /> Voltar para Revisão dos Vendidos</button>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ArrivalsBanner = ({ arrivals = [], onOpen, onSeeQueue }) => {
  if (!arrivals.length) return null;
  const novos = arrivals.filter((a) => a.is_new).length;
  return (
    <div className="rounded-lg border border-blue-400/35 bg-blue-400/[0.05] px-3.5 py-3" data-testid="svc-arrivals-banner">
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className="inline-flex items-center gap-2 text-[12.5px] font-black uppercase tracking-wide text-blue-400">
          <BellRing className="w-4 h-4" /> Veículos vendidos aguardando Service
        </span>
        <span className="ent-pill" data-testid="svc-arrivals-count">{arrivals.length}</span>
        {novos > 0 && <span className="ent-tag t-warn" data-testid="svc-arrivals-new">{novos} nas últimas 24h</span>}
        <span className="text-[11px] t-label ml-auto">Chegaram da Revisão dos Vendidos com o W.O. concluído</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 mt-2.5">
        {arrivals.slice(0, 8).map((a) => (
          <button key={a.deal_id} data-testid={`svc-arrival-${a.deal_id}`} onClick={() => onOpen(a.deal_id)}
            className="text-left rounded-md border border-border bg-card px-2.5 py-2 hover:border-blue-400/60 transition-colors">
            <div className="flex items-center gap-1.5">
              <p className="text-[12px] font-semibold truncate flex-1">{a.customer_name}</p>
              {a.is_new && <span className="ent-tag t-warn">NOVO</span>}
            </div>
            <p className="text-[11px] text-foreground/75 truncate">{a.vehicle_desc} <span className="mono t-label">· VIN {a.vin || '—'}</span></p>
            <p className="text-[10px] t-label mono">#{a.stock} · {a.hours_waiting === null ? '—' : a.hours_waiting < 1 ? 'agora' : a.hours_waiting < 24 ? `${a.hours_waiting}h na fila` : `${Math.floor(a.hours_waiting / 24)}d na fila`}
              {a.funding_pending && <span className="t-warn"> · funding pending</span>}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

const ServiceDelivery = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('inspection');
  const [sub, setSub] = useState('queue');
  const [inspId, setInspId] = useState(null);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState({ kpis: {}, deals: [], arrivals: [] });
  const [psCases, setPsCases] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const canInspect = SERVICE_EDIT_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: ps }] = await Promise.all([api.get('/service/delivery-queue'), api.get('/post_sale_cases')]);
      setData(r); setPsCases(ps);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);

  if (user && !user.permissions?.includes('view_service')) return <Navigate to="/app" replace />;

  const inService = data.deals.filter((d) => ['requested', 'in_progress'].includes(d.service_status));  const ready = data.deals.filter((d) => d.service_status === 'ready' && d.status !== 'DELIVERED');
  const delivered = data.deals.filter((d) => d.status === 'DELIVERED');
  const psOpen = psCases.filter((c) => !c.delivered);
  const psDelivered = psCases.filter((c) => c.delivered).slice(0, 6);
  const canDeliverPs = PS_DELIVER_ROLES.includes(user?.role);

  const deliverPs = async (c) => {
    try {
      await api.post(`/post-sale/cases/${c.id}/workshop`, { action: 'delivered' });
      toast({ title: '🏁 Pós-venda entregue', description: 'Caso movido para Delivered e arquivado no Pós-Venda.' });
      load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const dropToReady = async (dealId) => {
    const d = data.deals.find((x) => x.id === dealId);
    if (!d || !['requested', 'in_progress'].includes(d.service_status)) return;
    try {
      await api.post(`/deals/${dealId}/service/ready`);
      toast({ title: '🚗 READY FOR DELIVERY', description: 'Vendedor e gerência notificados.' });
      load();
    } catch (e) {
      toast({ title: 'Inspeção incompleta', description: e.response?.data?.detail || '' });
      setDetailId(dealId);
    }
  };

  const dropToDelivered = (dealId) => {
    const d = data.deals.find((x) => x.id === dealId);
    if (!d || d.status !== 'READY_FOR_DELIVERY') return;
    setDetailId(dealId);
    toast({ title: 'Registrar entrega', description: 'Preencha data, hora e presença do cliente para concluir.' });
  };

  return (
    <div className="space-y-4" data-testid="service-page">
      <div className="ent-tabs overflow-x-auto">
        {[['inspection', ClipboardCheck, 'Inspeção de Entrada'], ['sold', Car, 'Revisão dos Vendidos'], ['mech', Wrench, 'Fila do Mecânico'], ['delivery', Truck, 'Delivery Inspection'], ['ro', LifeBuoy, 'Repair Order / Pós-venda']].map(([k, Icon, l]) => (
          <button key={k} data-testid={`svc-tab-${k}`} onClick={() => { setTab(k); setInspId(null); }} className={`ent-tab whitespace-nowrap ${tab === k ? 'ent-tab-active' : ''}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
        ))}
      </div>

      {tab === 'inspection' && (inspId
        ? <InspectionDetail id={inspId} onBack={() => { setInspId(null); setReload((r) => r + 1); }} onChanged={() => setReload((r) => r + 1)} />
        : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <button data-testid="svc-sub-queue" onClick={() => setSub('queue')} className={`ent-btn ent-btn-sm ${sub === 'queue' ? 'ent-btn-primary' : ''}`}>Fila de inspeção</button>
              <button data-testid="svc-sub-recon" onClick={() => setSub('recon')} className={`ent-btn ent-btn-sm ${sub === 'recon' ? 'ent-btn-primary' : ''}`}>Recon</button>
            </div>
            {sub === 'queue'
              ? <InspectionQueue key={reload} onOpen={setInspId} canEdit={canInspect} userId={user?.user_id} />
              : <ReconBoard canEdit={canInspect} onOpenInspection={(iid) => { setSub('queue'); setInspId(iid); }} />}
          </div>
        ))}

      {tab === 'sold' && <SoldReview canEdit={canInspect} />}

      {tab === 'mech' && <MechanicQueue />}

      {tab === 'ro' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-orange-400/30 bg-orange-400/[0.04] p-4" data-testid="ro-postsale-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 text-sm font-black uppercase text-orange-400"><LifeBuoy className="w-4 h-4" /> Pós-Venda na Oficina</span>
              <span className="text-xs font-semibold text-orange-400 bg-orange-400/12 px-2 py-0.5 rounded-full">{psOpen.length} aberto{psOpen.length !== 1 ? 's' : ''}</span>
            </div>
            {psOpen.length === 0 && psDelivered.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/50 text-center py-4">Nenhum caso de pós-venda em aberto</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5">
                {psOpen.map((c) => <PsCard key={c.id} c={c} onDeliver={deliverPs} canDeliver={canDeliverPs} />)}
                {psDelivered.map((c) => <PsCard key={c.id} c={c} canDeliver={false} />)}
              </div>
            )}
          </div>
          <RepairOrders />
        </div>
      )}

      {tab === 'delivery' && (
        <>
          <ArrivalsBanner arrivals={data.arrivals || []} onSeeQueue={() => {}} onOpen={setDetailId} />
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <KPI label="Waiting Inspection" value={data.kpis.waiting_inspection} icon={Wrench} accent="bg-amber-400/10 text-amber-400" />
            <KPI label="In Progress" value={data.kpis.in_progress} icon={ClipboardCheck} accent="bg-blue-400/10 text-blue-400" />
            <KPI label="Ready for Delivery" value={data.kpis.ready_for_delivery} icon={Truck} accent="bg-emerald-400/10 text-emerald-400" />
            <KPI label="Delivered Today" value={data.kpis.delivered_today} icon={Flag} accent="bg-muted/40 text-foreground/65" />
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            <Column title="Aguardando Delivery Inspection" cls="text-blue-400 bg-blue-400/10" deals={inService} onOpen={setDetailId} testid="svc-col-service" />
            <Column title="Ready for Delivery" cls="text-emerald-400 bg-emerald-400/10" deals={ready} onOpen={setDetailId} testid="svc-col-ready" onDropCard={dropToReady} />
            <Column title="Delivered (recentes)" cls="text-muted-foreground bg-muted/40" deals={delivered} onOpen={setDetailId} testid="svc-col-delivered" onDropCard={dropToDelivered} />
          </div>
          <InspectionDialog dealId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
        </>
      )}
    </div>
  );
};

export default ServiceDelivery;
